import {useMemo} from "react";
import {css} from "@panda/css";
import {formatISO, parseISO} from "date-fns";
import type {Options, Point, SeriesLineOptions} from "highcharts";

import {CenterBox} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {Chart, getDefaultXAxisOptions, getDefaultYAxisOptions} from "@pg-mono/charts";
import {formatPL, pluralize, priceM2Format} from "@pg-mono/string-utils";

import {IRcnHistoryPoint} from "../../hooks/use_rcn_history";
import {RcnPriceStatsGrain} from "../../types/IRcnPriceStats";
import {formatRcnHistoryPeriod} from "../../utils/format_rcn_history_period";
import {formatQuarterLabelForDate} from "../../utils/format_rcn_period_label";
import {RCN_HISTORY_GRAIN} from "../../utils/get_rcn_params";
import {ISeriesTrend} from "../../utils/get_series_trend";
import {formatPriceAxisLabel, getPriceAxisAllowDecimals} from "../../utils/price_axis";

const CHART_HEIGHT = "280px";

// A point this close to the trend (zł/m², half the precision of the prices shown) lies on it - with two points the line
// runs through both, and floating point alone must not paint one of them "below".
const ON_TREND_TOLERANCE = 0.5;

// Ticks fall on the grain's periods or on years - never on days or weeks, which adjacent months would get otherwise.
const X_AXIS_UNITS: Record<RcnPriceStatsGrain, [string, number[] | null][]> = {
    month: [
        ["month", [1, 2, 3, 4, 6]],
        ["year", null]
    ],
    quarter: [
        ["month", [3, 6]],
        ["year", null]
    ],
    year: [["year", null]]
};

// "gru 2020", "IV kw. 2020", "2020"
const formatAxisLabel = (timestamp: number, grain: RcnPriceStatsGrain): string => {
    const date = new Date(timestamp);

    switch (grain) {
        case "month":
            return formatPL(date, "LLL yyyy");
        case "quarter":
            return formatQuarterLabelForDate(formatISO(date, {representation: "date"}));
        default:
            return formatPL(date, "yyyy");
    }
};

interface Props {
    points: IRcnHistoryPoint[];
    trend: ISeriesTrend | null;
    isLoading: boolean;
    /** The grain the points were published in - decides the ticks, their labels and the tooltip's period. */
    grain?: RcnPriceStatsGrain;
}

interface IPricePointCustom {
    date: string;
    transactions: number;
}

export const PricesAddressTrendChart = (props: Props) => {
    const {points, trend, isLoading, grain = RCN_HISTORY_GRAIN} = props;

    const options = useMemo<Options>(() => {
        const priceSeries: SeriesLineOptions = {
            type: "line",
            name: "Cena za m²",
            color: "var(--colors-secondary)",
            lineWidth: 2,
            states: {hover: {lineWidth: 2}},
            data: points.map((point) => {
                const x = parseISO(point.date).getTime();
                const isAboveTrend = trend ? point.value >= trend.valueAt(x) - ON_TREND_TOLERANCE : true;

                return {
                    x,
                    y: point.value,
                    marker: {
                        radius: 6,
                        symbol: "circle",
                        lineWidth: 1,
                        lineColor: "var(--colors-secondary)",
                        fillColor: isAboveTrend ? "var(--colors-success)" : "var(--colors-warning)"
                    },
                    custom: {date: point.date, transactions: point.transactions} satisfies IPricePointCustom
                };
            })
        };

        const trendSeries: SeriesLineOptions[] = trend
            ? [
                  {
                      type: "line",
                      name: "Trend",
                      dashStyle: "Dash",
                      color: "var(--colors-gray-700)",
                      lineWidth: 2,
                      marker: {enabled: false},
                      enableMouseTracking: false,
                      data: [
                          [parseISO(trend.firstDate).getTime(), trend.valueAt(parseISO(trend.firstDate).getTime())],
                          [parseISO(trend.lastDate).getTime(), trend.valueAt(parseISO(trend.lastDate).getTime())]
                      ]
                  }
              ]
            : [];

        return {
            title: {text: ""},
            legend: {enabled: false},
            xAxis: {
                ...getDefaultXAxisOptions(),
                type: "datetime",
                units: X_AXIS_UNITS[grain],
                // Yearly points are few and evenly spaced, so a tick under each one labels every year - Highcharts' own
                // ticks skip the first and the last. A lone point of any grain leaves no range to tick: its own date labels it.
                ...(grain === "year" || points.length === 1 ? {tickPositions: points.map((point) => parseISO(point.date).getTime())} : {}),
                labels: {
                    enabled: true,
                    style: {color: "var(--colors-gray-700)", fontSize: "1.106rem"},
                    formatter: ({value}: {value: string | number}) => formatAxisLabel(Number(value), grain)
                }
            },
            yAxis: {
                ...getDefaultYAxisOptions(),
                gridLineColor: "var(--colors-gray-400)",
                allowDecimals: getPriceAxisAllowDecimals(points.map((point) => point.value)),
                labels: {
                    enabled: true,
                    style: {color: "var(--colors-gray-700)", fontSize: "1.106rem"},
                    formatter: ({value}: {value: string | number}) => formatPriceAxisLabel(Number(value))
                }
            },
            series: [priceSeries, ...trendSeries],
            tooltip: {
                borderColor: "transparent",
                backgroundColor: "var(--colors-white)",
                style: {color: "var(--colors-secondary)", fontSize: "1.2rem"},
                headerFormat: "",
                pointFormatter: function (this: Point) {
                    const custom = this.options.custom as IPricePointCustom | undefined;
                    const price = `<b>${priceM2Format(this.y ?? 0, {precision: 0})}</b>`;

                    if (!custom) {
                        return price;
                    }

                    return `${formatRcnHistoryPeriod(custom.date, grain)}<br/>${price}<br/>${getTransactionsText(custom.transactions)}`;
                }
            }
        };
    }, [points, trend, grain]);

    return (
        <div className={chartHolderStyle}>
            <Chart
                height={CHART_HEIGHT}
                options={options}
                loader={
                    isLoading ? (
                        <CenterBox height={CHART_HEIGHT}>
                            <Loader size="lg" />
                        </CenterBox>
                    ) : null
                }
            />
        </div>
    );
};

const transactionsPluralize = pluralize(["transakcja", "transakcje", "transakcji"]);

const getTransactionsText = (transactions: number): string => `${transactions} ${transactionsPluralize(transactions)}`;

const chartHolderStyle = css({
    width: "100%"
});
import {useMemo} from "react";
import {css} from "@panda/css";
import {formatISO, parseISO} from "date-fns";
import type {Options, Point, SeriesLineOptions} from "highcharts";

import {CenterBox} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {Chart, getDefaultXAxisOptions, getDefaultYAxisOptions} from "@pg-mono/charts";
import {formatPL, pluralize, priceM2Format} from "@pg-mono/string-utils";

import {IRcnHistoryPoint} from "../../hooks/use_rcn_history";
import {RcnPriceStatsGrain} from "../../types/IRcnPriceStats";
import {formatRcnHistoryPeriod} from "../../utils/format_rcn_history_period";
import {formatQuarterLabelForDate} from "../../utils/format_rcn_period_label";
import {RCN_HISTORY_GRAIN} from "../../utils/get_rcn_params";
import {ISeriesTrend} from "../../utils/get_series_trend";
import {formatPriceAxisLabel, getPriceAxisAllowDecimals} from "../../utils/price_axis";

const CHART_HEIGHT = "280px";

// A point this close to the trend (zł/m², half the precision of the prices shown) lies on it - with two points the line
// runs through both, and floating point alone must not paint one of them "below".
const ON_TREND_TOLERANCE = 0.5;

// Ticks fall on the grain's periods or on years - never on days or weeks, which adjacent months would get otherwise.
const X_AXIS_UNITS: Record<RcnPriceStatsGrain, [string, number[] | null][]> = {
    month: [
        ["month", [1, 2, 3, 4, 6]],
        ["year", null]
    ],
    quarter: [
        ["month", [3, 6]],
        ["year", null]
    ],
    year: [["year", null]]
};

// "gru 2020", "IV kw. 2020", "2020"
const formatAxisLabel = (timestamp: number, grain: RcnPriceStatsGrain): string => {
    const date = new Date(timestamp);

    switch (grain) {
        case "month":
            return formatPL(date, "LLL yyyy");
        case "quarter":
            return formatQuarterLabelForDate(formatISO(date, {representation: "date"}));
        default:
            return formatPL(date, "yyyy");
    }
};

interface Props {
    points: IRcnHistoryPoint[];
    trend: ISeriesTrend | null;
    isLoading: boolean;
    /** The grain the points were published in - decides the ticks, their labels and the tooltip's period. */
    grain?: RcnPriceStatsGrain;
}

interface IPricePointCustom {
    date: string;
    transactions: number;
}

export const PricesAddressTrendChart = (props: Props) => {
    const {points, trend, isLoading, grain = RCN_HISTORY_GRAIN} = props;

    const options = useMemo<Options>(() => {
        const priceSeries: SeriesLineOptions = {
            type: "line",
            name: "Cena za m²",
            color: "var(--colors-secondary)",
            lineWidth: 2,
            states: {hover: {lineWidth: 2}},
            data: points.map((point) => {
                const x = parseISO(point.date).getTime();
                const isAboveTrend = trend ? point.value >= trend.valueAt(x) - ON_TREND_TOLERANCE : true;

                return {
                    x,
                    y: point.value,
                    marker: {
                        radius: 6,
                        symbol: "circle",
                        lineWidth: 1,
                        lineColor: "var(--colors-secondary)",
                        fillColor: isAboveTrend ? "var(--colors-success)" : "var(--colors-warning)"
                    },
                    custom: {date: point.date, transactions: point.transactions} satisfies IPricePointCustom
                };
            })
        };

        const trendSeries: SeriesLineOptions[] = trend
            ? [
                  {
                      type: "line",
                      name: "Trend",
                      dashStyle: "Dash",
                      color: "var(--colors-gray-700)",
                      lineWidth: 2,
                      marker: {enabled: false},
                      enableMouseTracking: false,
                      data: [
                          [parseISO(trend.firstDate).getTime(), trend.valueAt(parseISO(trend.firstDate).getTime())],
                          [parseISO(trend.lastDate).getTime(), trend.valueAt(parseISO(trend.lastDate).getTime())]
                      ]
                  }
              ]
            : [];

        return {
            title: {text: ""},
            legend: {enabled: false},
            xAxis: {
                ...getDefaultXAxisOptions(),
                type: "datetime",
                units: X_AXIS_UNITS[grain],
                // Yearly points are few and evenly spaced, so a tick under each one labels every year - Highcharts' own
                // ticks skip the first and the last. A lone point of any grain leaves no range to tick: its own date labels it.
                ...(grain === "year" || points.length === 1 ? {tickPositions: points.map((point) => parseISO(point.date).getTime())} : {}),
                labels: {
                    enabled: true,
                    style: {color: "var(--colors-gray-700)", fontSize: "1.106rem"},
                    formatter: ({value}: {value: string | number}) => formatAxisLabel(Number(value), grain)
                }
            },
            yAxis: {
                ...getDefaultYAxisOptions(),
                gridLineColor: "var(--colors-gray-400)",
                allowDecimals: getPriceAxisAllowDecimals(points.map((point) => point.value)),
                labels: {
                    enabled: true,
                    style: {color: "var(--colors-gray-700)", fontSize: "1.106rem"},
                    formatter: ({value}: {value: string | number}) => formatPriceAxisLabel(Number(value))
                }
            },
            series: [priceSeries, ...trendSeries],
            tooltip: {
                borderColor: "transparent",
                backgroundColor: "var(--colors-white)",
                style: {color: "var(--colors-secondary)", fontSize: "1.2rem"},
                headerFormat: "",
                pointFormatter: function (this: Point) {
                    const custom = this.options.custom as IPricePointCustom | undefined;
                    const price = `<b>${priceM2Format(this.y ?? 0, {precision: 0})}</b>`;

                    if (!custom) {
                        return price;
                    }

                    return `${formatRcnHistoryPeriod(custom.date, grain)}<br/>${price}<br/>${getTransactionsText(custom.transactions)}`;
                }
            }
        };
    }, [points, trend, grain]);

    return (
        <div className={chartHolderStyle}>
            <Chart
                height={CHART_HEIGHT}
                options={options}
                loader={
                    isLoading ? (
                        <CenterBox height={CHART_HEIGHT}>
                            <Loader size="lg" />
                        </CenterBox>
                    ) : null
                }
            />
        </div>
    );
};

const transactionsPluralize = pluralize(["transakcja", "transakcje", "transakcji"]);

const getTransactionsText = (transactions: number): string => `${transactions} ${transactionsPluralize(transactions)}`;

const chartHolderStyle = css({
    width: "100%"
});
